import React, { Suspense, useState, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { hydrateRoot } from 'react-dom/client'
import { useSSR } from 'react-i18next'
import { loadableReady } from '@loadable/component'
import loadable from '@loadable/component'
import TagManager from 'react-gtm-module'
import {
  isToEnableSSR,
  APPConfig,
  convertToBoolean,
  getCookie,
  setCookie,
  deleteCookie,
} from 'config/appConfig'
import i18n, { initI18nForClient } from 'config/i18nConfig'
import { setAppConfigByStore } from 'config/storeController'
import { getLocaleCodeFromUrl } from 'src/utils/localeUtils'
import { Routes } from 'src/routes'
import {
  ApiContent,
  IS_BROWSER,
  getLocalePathParam,
  setMysiteOrPersonalOffer,
  setFraudDetectionCbkStatus,
  setUTMInfo,
  trackErrorInInstana,
  isServerPages,
  customGdprPopup,
  enableMysiteRedesignFeature,
  enablePORedesignFeature,
  isPreviewMode,
  enableHeaderMFE,
  enableHeaderMFESSR,
  getLocalStorage,
  setLocalStorage,
  deleteFromLocalStorage,
} from 'src/utils'
import { translationStore } from 'src/models/TranslationStore/TranslationStore'
import { detectIncognito } from 'detectincognitojs'
import { initSplunk } from 'src/utils/splunk.js'
import { createInsiderObject, trackInsiderUserObject } from 'src/utils/insider'
import { registerInsiderAddToCartGlobal } from 'src/utils/insiderAddToCartBridge'
import { customerContainer, cartContainer } from 'src/models'
import {
  isConsentAcceptedForTracking,
  initBigIdConsent,
} from 'src/utils/consent'
import { pageNames } from 'src/routes/pathParams'
import { redirectToLoginCallbackWithLocale } from 'src/utils/signInUtils'
import {
  getBaseName,
  isEquinoxMysite,
  isEquinoxPOSite,
  loadMySiteData,
} from 'src/utils/mySiteUtils'
import './styles.scss'
import { REGEX_BARE_PERCENT } from 'src/utils/constant'
import { preloadRemote } from 'src/views/components/MicroFrontends/RemoteLoader'
import { isSamePath } from 'src/views/components/MicroFrontends/MicroFrontendUtils'

// Upper bound on how long hydration waits for the server-rendered MFEs'
// remote modules. The preload is started before the store/customer/cart
// initialisation below, so it normally resolves long before hydration is
// reached and this cap never applies. It only bounds the tail (slow network,
// degraded MFE CDN), where blocking hydration would leave the whole page —
// header, search, cart — non-interactive. Past the cap the host hydrates with
// the SSR markup kept as static HTML and the MFE mounts as soon as its module
// arrives (RemoteLoader adopts a late preload).
const MFE_PRELOAD_TIMEOUT_MS = 1500

/**
 * Starts loading the remote module of every MFE the server rendered for this
 * URL (remoteEntry + exposed chunk) so that, by the time hydrateRoot() runs,
 * RemoteLoader can render the real component on its first pass and hydrate
 * the server-rendered MFE markup instead of discarding it. Resolves when all
 * loads settle; never rejects.
 */
function preloadServerRenderedRemotes() {
  if (!IS_BROWSER || typeof __initialData === 'undefined') {
    return Promise.resolve()
  }
  const mfeServerDataMap = __initialData?.mfeServerDataMap || {}
  const pathname = window.location.pathname || ''
  const loads = Object.values(mfeServerDataMap)
    .filter(
      serverData =>
        serverData?._clientRemoteConfig?.name &&
        serverData?._clientRemoteConfig?.entry &&
        isSamePath(serverData?._hostMeta?.url, pathname)
    )
    .map(serverData => preloadRemote(serverData._clientRemoteConfig))
  return Promise.all(loads)
}

function waitAtMost(promise, timeoutMs) {
  let timerId
  // The timer is cleared once the race settles so a preload that resolves
  // quickly does not leave a pending timeout behind.
  return Promise.race([
    promise,
    new Promise(resolve => {
      timerId = setTimeout(resolve, timeoutMs)
    }),
  ]).finally(() => clearTimeout(timerId))
}

let timeoutId
let NsUtil
let isGDPRInitialize = false

function LegacyNsUtilInitializer() {
  if (!NsUtil) {
    NsUtil = loadable.lib(() => import('@nuskin/ns-util'), {
      ssr: false,
    })
  }

  return (
    <NsUtil>
      {({ init: nsUtilInit }) => {
        nsUtilInit()
        return ''
      }}
    </NsUtil>
  )
}

export function CrossTabLogoutInitializer() {
  useEffect(() => {
    let cleanup
    let isMounted = true

    import('src/utils/crossTabLogout')
      .then(({ initializeCrossTabLogout }) => {
        if (isMounted) {
          cleanup = initializeCrossTabLogout()
        }
      })
      .catch(error => {
        console.error('Failed to load crossTabLogout module:', error)
      })

    return () => {
      isMounted = false
      cleanup?.()
    }
  }, [])

  return null
}

// Ignore hydration errors
window.addEventListener('error', event => {
  if (
    event.error?.message?.includes('#418') ||
    event.error?.message?.includes('#423')
  ) {
    event.preventDefault()
    return false
  }
})

// Fix for Chunk error issue if we redeploy a new build
// Window event listener is not triggering so used console error
const origin = console.error
console.error = (error, ...rest) => {
  // Older chunk is failed due to new version
  if (/chunk [\d]+ failed/.test(error?.message)) {
    cacheBurst({ error })
  } else {
    origin(error, ...rest)
  }
}

// We don't need to redirect to region selector page.
// function RedirectToRegion() {
//   const redirectTo = () => {
//     if (IS_BROWSER) {
//       const storeId = getLocalStorage('storeId')
//       // const appLocale = getLocalStorage('defaultLocale')
//       const appLocale = getLocaleCodeFromUrl({
//         url: location.href,
//         at: 'pathParam',
//         defaultLocale: 'en_US',
//         isReverseType: true,
//       })

//       if (
//         (!storeId || !appLocale) &&
//         !window.location.href.includes('selectregion')
//       ) {
//         window.location.href = `${window.location.origin}/selectregion`
//       }
//     }
//   }

//   redirectTo()
//   return <></>
// }

function checkAndLoadClientTranslation() {
  // @translation_cs
  const enableTranslationFromContentStack = convertToBoolean(
    APPConfig?.getAppConfig()?.enableTranslationFromContentStack
  )
  const isClientTranslationLoadedAlready = enableTranslationFromContentStack
    ? translationStore.isClientTranslationLoadedAlready
    : i18n.hasLoadedNamespace('csr-resource')
  if (isClientTranslationLoadedAlready) {
    document.removeEventListener('click', checkAndLoadClientTranslation)
    return
  } else {
    if (enableTranslationFromContentStack) {
      translationStore.fetchTranslationForBrowser(true)
    } else {
      i18n.loadNamespaces('csr-resource')
    }
  }
}

function OnScrollChanges() {
  const acceritfySrcUrl = APPConfig?.getAppConfig()?.acceritfySrcUrl

  const [scroll, setScroll] = useState(false)

  const handleScroll = e => {
    setScroll(true)
  }
  if (window && window.addEventListener) {
    window.addEventListener('scroll', handleScroll)
  }

  useEffect(() => {
    if (scroll) {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  if (scroll) {
    checkAndLoadClientTranslation()
  }
  return <></>
}

function showCookieConsent() {
  try {
    // Using document.getElementById, since the DOM is coming from 'getHtml'
    document.getElementById('consent_blackbar').style.display = 'block'
    // document.getElementById('teconsent').style.display = 'block'
  } catch (e) {
    console.log(e)
  }
}

function FraudDetectionScriptTag() {
  // Using document.head, since the DOM is coming from 'getHtml'
  const acceritfySrcUrl = APPConfig?.getAppConfig()?.acceritfySrcUrl
  if (acceritfySrcUrl) {
    const script = document.createElement('script')
    script.src = acceritfySrcUrl
    script.id = 'bcn'
    script.setAttribute('dvc', 'a')
    script.setAttribute('dvct', '500')
    script.defer = true
    script.type = 'text/javascript'
    script.onload = initFraudDetectionCbk
    const headTag = document.getElementsByTagName('head')[0]
    headTag.appendChild(script)
  }

  return <></>
}

function InsiderScriptTag() {
  createInsiderObject()

  // Using document.head, since the DOM is coming from 'getHtml'
  const insiderConfig = APPConfig?.getAppConfig()?.insider
  const enableInsider = convertToBoolean(insiderConfig?.enableInsider) || false
  const partnerName = insiderConfig?.partnerName
  const partnerId = insiderConfig?.partnerId

  if (enableInsider && partnerName && partnerId) {
    const script = document.createElement('script')
    script.src = `https://${partnerName}.api.useinsider.com/ins.js?id=${partnerId}`
    script.id = 'insider'
    script.async = true
    script.defer = true
    script.type = 'text/javascript'
    // script.onload = initFraudDetectionCbk
    const headTag = document.getElementsByTagName('head')[0]
    headTag.appendChild(script)
  }

  return <></>
}

function matchWildcard() {
  const url = document.referrer
  const whitelistIframePatterns =
    APPConfig?.getAppConfig()?.whitelistIframes || []

  if (!url) return false
  try {
    const { hostname } = new URL(url)
    return whitelistIframePatterns.some(pattern => {
      let patternHost = pattern.replace(/^https?:\/\//, '')

      if (!patternHost.includes('*')) {
        return hostname === patternHost
      }

      // Safer wildcard matching without RegExp constructor
      return matchWildcardPattern(hostname, patternHost)
    })
  } catch (e) {
    return false
  }
}

/**
 * Safely matches a hostname against a wildcard pattern without using RegExp constructor
 * @param {string} hostname - The hostname to test
 * @param {string} pattern - The wildcard pattern (may contain *)
 * @returns {boolean} - True if hostname matches pattern
 */
function matchWildcardPattern(hostname, pattern) {
  // Validate inputs and limit complexity to prevent ReDoS
  if (
    typeof hostname !== 'string' ||
    typeof pattern !== 'string' ||
    pattern.length > 100 ||
    (pattern.match(/\*/g) || []).length > 5
  ) {
    return false
  }

  const segments = pattern.split('*').filter(s => s !== '')
  const lowerHostname = hostname.toLowerCase()

  // No wildcards - exact match
  if (!pattern.includes('*')) {
    return lowerHostname === pattern.toLowerCase()
  }

  let pos = 0
  const startsWithWildcard = pattern.startsWith('*')
  const endsWithWildcard = pattern.endsWith('*')

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i].toLowerCase()
    const isFirst = i === 0 && !startsWithWildcard
    const isLast = i === segments.length - 1 && !endsWithWildcard

    if (isFirst) {
      if (!lowerHostname.startsWith(segment)) return false
      pos = segment.length
    } else if (isLast) {
      if (
        !lowerHostname.endsWith(segment) ||
        lowerHostname.length - segment.length < pos
      )
        return false
    } else {
      pos = lowerHostname.indexOf(segment, pos)
      if (pos === -1) return false
      pos += segment.length
    }
  }

  return true
}

/**
 * Fixes invalid percent-encoding in URL pathname (MDIGI-14406, MDIGI-14407)
 */
function validateAndSanitizeUrl() {
  if (!IS_BROWSER) return true

  try {
    // Try to decode the pathname - if it throws, we have invalid encoding
    decodeURIComponent(window.location.pathname)
  } catch (error) {
    if (error instanceof URIError) {
      trackErrorInInstana({
        errorReport: 'Invalid percent-encoding in URL',
        errorData: {
          pathname: window.location.pathname,
          error: error.message,
        },
      })

      // Encode bare % characters as %25 (e.g., /search/10% → /search/10%25)
      const sanitizedPathname = window.location.pathname.replace(
        REGEX_BARE_PERCENT,
        '%25'
      )

      const newUrl =
        sanitizedPathname + window.location.search + window.location.hash

      // Redirect to sanitized URL
      window.location.replace(newUrl)
      return false
    }
    // Re-throw non-URIError errors
    throw error
  }
  return true
}

function WrapperComponent() {
  // Validate URL before BrowserRouter to prevent URIError
  if (!validateAndSanitizeUrl()) {
    return null // URL is being sanitized, don't render yet
  }

  const isInPreviewIframe =
    IS_BROWSER &&
    (isEquinoxMysite() || isEquinoxPOSite()) &&
    window?.location?.search?.includes('inIframe')

  if (
    isLoadingInIframe() &&
    IS_BROWSER &&
    !isInPreviewIframe &&
    window.parent !== window &&
    !matchWildcard()
  ) {
    console.warn('Site loading in iframe')
    top.location = self.location
  }

  setRedirectedAppsCookie()
  setUTMInfo()
  const tagManagerArgs = {
    gtmId: APPConfig?.getAppConfig()?.gtmKey,
  }
  TagManager.initialize(tagManagerArgs)

  // const isUserConsent = getLocalStorage('hasUserConsent')
  let baseName = getLocalePathParam()
  /*if mysite is present in url then we are appedning mysite to path param with subdomain */
  if (
    (enableMysiteRedesignFeature() || enablePORedesignFeature()) &&
    IS_BROWSER
  ) {
    baseName = getBaseName(baseName)
  }
  let isNativeAppQueryParamString =
    new URL(location?.href)?.searchParams?.get('isNativeApp') || false
  if (isNativeAppQueryParamString && IS_BROWSER) {
    window.isNativeApp = true
  }

  const isNativeMobileApp =
    window?.isNativeApp == true || window?.isVeraApp == true
  const enableCrossTabLogout = convertToBoolean(
    APPConfig?.getAppConfig()?.enableCrossTabLogout
  )

  if (!isNativeMobileApp) {
    loadGDPRScript()
    showCookieConsent()
  }

  hideChatbot()

  const isCookieEnabled = navigator.cookieEnabled
  document.addEventListener('click', checkAndLoadClientTranslation)
  const locale = getLocaleCodeFromUrl()

  return (
    <>
      {/* <RedirectToRegion /> */}
      <OnScrollChanges />
      <FraudDetectionScriptTag />
      <InsiderScriptTag />
      {!isNativeMobileApp && enableCrossTabLogout && (
        <CrossTabLogoutInitializer />
      )}
      {!isNativeMobileApp &&
        !enableCrossTabLogout &&
        isCookieEnabled &&
        locale && <LegacyNsUtilInitializer />}
      <BrowserRouter basename={baseName}>
        <Routes />
      </BrowserRouter>
    </>
  )
}

function ServerApp() {
  let providerValue = {}
  if (typeof __initialData !== 'undefined') {
    providerValue = __initialData
  }
  const enableTranslationFromContentStack = convertToBoolean(
    APPConfig?.getAppConfig()?.enableTranslationFromContentStack
  )
  if (!enableTranslationFromContentStack) {
    useSSR(window.initialI18nStore || {}, window.initialLanguage || '')
  }
  // __initialData = ''
  // https://github.com/facebook/react/issues/10879 - Expected error in dev mode

  return (
    <ApiContent.Provider value={providerValue}>
      <Suspense fallback="">
        <WrapperComponent />
      </Suspense>
    </ApiContent.Provider>
  )
}

/**
 * Initializes the fraud detection callback.
 *
 * Sets a timeout to wait 700ms, then if in the browser calls the Beacon
 * setSubmissionCallback method to set the callback that will receive the
 * fraud detection status.
 */
function initFraudDetectionCbk() {
  setTimeout(() => {
    if (IS_BROWSER) {
      window._bcn?.dvc?.setSubmissionCallback(status => {
        setFraudDetectionCbkStatus(status)
      })
    }
  }, 700)
  return null
}

function renderClientApp() {
  const ClientApp = WrapperComponent
  const root = createRoot(document.getElementById('app-root'))

  root.render(
    <Suspense fallback="">
      <ClientApp />
    </Suspense>
  )
}

function hasValidStoreConfigInBrowser() {
  if (!IS_BROWSER) {
    return false
  }

  const storeConfig = window?.STORE_CONFIG || {}
  const storeIdFromConfig =
    storeConfig?.activeStoreId ||
    storeConfig?.activeLoadParams?.queryParams?.storeId ||
    storeConfig?.activeStoreResponse?.loadParamsForDebugging?.queryParams
      ?.storeId

  return Boolean(storeIdFromConfig)
}

async function ensureStoreConfigFromBrowser() {
  if (!IS_BROWSER || hasValidStoreConfigInBrowser()) {
    return
  }

  try {
    const recoveryPayload = {
      eventName: 'csr_fallback_triggered',
      pageUrl: window.location.href,
      source: 'store_config_recovery',
    }
    trackErrorInInstana({
      errorReport: 'CSR Fallback Triggered',
      errorData: recoveryPayload,
    })

    await setAppConfigByStore({
      host: window.location.host || document.domain,
      hostName: window.location.hostname || document.domain,
      pageUrl: window.location.href,
      localeProp: 'defaultLocale',
      cbk: async () => {
        window.APP_CONFIG = APPConfig?.getAppConfig?.() || window.APP_CONFIG
        const latestStoreConfig =
          globalThis?.STORE_CONFIG || window?.STORE_CONFIG || {}
        if (Object.keys(latestStoreConfig).length > 0) {
          window.STORE_CONFIG = latestStoreConfig
        }
      },
    })
  } catch (error) {
    const errorPayload = {
      eventName: 'csr_fallback_failed',
      pageUrl: window.location.href,
      source: 'store_config_recovery',
      errorMessage: error?.message || '',
      errorName: error?.name || '',
    }
    trackErrorInInstana({
      errorReport: 'CSR Fallback Failed',
      errorData: errorPayload,
    })
    console.warn('error in ensureStoreConfigFromBrowser', error)
  }
}

/**
 * Helper to determine if cart should be initialized for header MFE
 * Excludes checkout pages that handle cart initialization separately
 */
function initializeCartForHeaderMFE() {
  if (!IS_BROWSER) return false
  if (!enableHeaderMFE() && !enableHeaderMFESSR()) return false

  const isKlarnaCheckout = window?.location?.pathname?.includes(
    pageNames.klarnaCheckout
  )
  const isVenmoSuccess = window?.location?.hash
    ?.toLowerCase?.()
    ?.includes('venmosuccess')
  const isGPSExpressCheckout =
    getLocalStorage('gpsRedirection') &&
    window?.location?.pathname?.includes(pageNames.expressCheckout)

  return !isKlarnaCheckout && !isVenmoSuccess && !isGPSExpressCheckout
}

async function initCustomerInfo() {
  // initializing customer information to avoid Mobx reaction issues
  // on page load
  await customerContainer?.getProfile()
  const pageUrl = IS_BROWSER ? window?.location?.href : ''
  !isPreviewMode(pageUrl) &&
    initBigIdConsent({ userData: customerContainer.profileResponse })
  customerContainer.isGDPRAccepted = isConsentAcceptedForTracking()
  trackInsiderUserObject({
    userData: customerContainer.profileResponse,
    eventType: 'user',
  })
}

async function initCartInfo() {
  if (
    IS_BROWSER &&
    getLocalStorage('isFromAlipayPayment') &&
    !window?.location?.href?.includes('alipay_status')
  ) {
    setLocalStorage('isExpressCart', false)
    deleteFromLocalStorage('isFromAlipayPayment')
  }
  // Initialize cart - moved from Session.init() to prevent breaking when NuskinHeader is deprecated
  // Only execute when header MFE is enabled (enableHeaderMFE or enableHeaderMFESSR)
  if (initializeCartForHeaderMFE()) {
    try {
      await cartContainer.viewCart(null, true)
    } catch (error) {
      // Don't break app boot if cart fetch fails - cart will retry on user interaction
      console.error('Cart initialization failed during app boot:', error)
      trackErrorInInstana({
        errorReport: 'Cart initialization failed in initCartInfo',
        errorData: {
          errorMessage: error?.message || '',
          errorName: error?.name || '',
          stack: error?.stack || '',
        },
      })
    }
  }
}

/**
 * Store front initiates here
 * @date 2023/8/17 - 14:16:01
 */
async function initApp() {
  const isPageRedirecting = await redirectToLoginCallbackWithLocale()

  if (isPageRedirecting) {
    return
  }

  if (isToEnableSSR) {
    // Kick off the MFE module downloads now so they overlap with the store /
    // customer / cart initialisation below instead of starting after it.
    const remotesPreload = preloadServerRenderedRemotes()
    loadableReady(async () => {
      await ensureStoreConfigFromBrowser()
      await initCustomerInfo()
      // registerInsiderAddToCartGlobal is called in both the SSR (loadableReady)
      // and CSR (setAppConfigByStore cbk) paths to ensure the contract is always
      // available after app initialisation regardless of render mode.
      // The function is idempotent — it guards against double-registration internally.
      registerInsiderAddToCartGlobal()
      /* Initialize mysite URL status early to cache the result*/
      await loadMySiteData()
      // Initialize cart
      await initCartInfo()
      const enableTranslationFromContentStack = convertToBoolean(
        APPConfig?.getAppConfig()?.enableTranslationFromContentStack
      )
      if (enableTranslationFromContentStack) {
        // @translation_cs
        if (!isServerPages()) {
          // for server pages, csr data will be loaded on scroll/click
          // in function checkAndLoadClientTranslation
          await translationStore.fetchTranslationForBrowser(true)
        }
      } else {
        initI18nForClient(true)
      }
      await waitAtMost(remotesPreload, MFE_PRELOAD_TIMEOUT_MS)
      // hydrate(<ServerApp />, document.getElementById('app-root'))
      hydrateRoot(document.getElementById('app-root'), <ServerApp />)
    })
  } else {
    /**
     * @note
     * CSR => Client Side Rendering
     */
    if (IS_BROWSER) {
      const appLocale = getLocaleCodeFromUrl({
        url: location.href,
        at: 'pathParam',
        defaultLocale: 'en_US',
        isReverseType: true,
      })

      document.querySelector('body').setAttribute('id', appLocale)
      setAppConfigByStore({
        host: document.domain,
        pageUrl: window.location.href,
        localeProp: 'defaultLocale',
        cbk: async () => {
          /**
           * @note
           * rendering the app only after
           * - locale chosen
           * - store settings collected
           */
          await initCustomerInfo()
          registerInsiderAddToCartGlobal()
          /* Initialize mysite URL status early to cache the result*/
          await loadMySiteData()
          // Initialize cart
          await initCartInfo()
          const enableTranslationFromContentStack = convertToBoolean(
            APPConfig?.getAppConfig()?.enableTranslationFromContentStack
          )
          if (enableTranslationFromContentStack) {
            //  for local builds
            await translationStore.fetchTranslationForBrowser()
          } else {
            initI18nForClient(false)
          }
          renderClientApp()
        },
      })
    }
  }

  setMysiteOrPersonalOffer()
  showConfigsAndVersion()
  initSplunk()
}

initApp()

function isLoadingInIframe() {
  if (IS_BROWSER) {
    const siteUsingIframe = document.getElementsByTagName('iframe').length
    const totalIframeLength = window.parent.frames.length
    if (totalIframeLength !== siteUsingIframe) {
      return true
    }
  }
  return false
}

function cacheBurst(options) {
  const { error } = options || {}
  trackErrorInInstana({ errorReport: 'Chunk failed to load', errorData: error })
  const cacheBurst = new Date().getTime()
  const url = new URL(window.location.href)
  url.searchParams.set('t', cacheBurst)
  window.location.href = url.href
}

async function loadGDPRScript() {
  const result = await detectIncognito()
  const { gdpr } = APPConfig?.getAppConfig()
  const isToEnableGDPR = convertToBoolean(gdpr?.enableIncognito || false)

  if (
    isToEnableGDPR &&
    result.isPrivate &&
    result.browserName === 'Safari' &&
    isGDPRInitialize === false
  ) {
    isGDPRInitialize = true
    customGdprPopup()
  }
}

function checkTrustArcLoad() {
  const { gdpr } = APPConfig?.getAppConfig()
  const timeoutDuration = parseInt(gdpr?.timeoutDuration || 6000, 10)

  const checkInterval = setInterval(() => {
    if (window?.truste && window?.truste?.eu) {
      clearInterval(checkInterval)
      clearTimeout(timeoutId)
      return true
    }
  }, 100)

  timeoutId = setTimeout(() => {
    clearInterval(checkInterval)
    if (!window?.truste) {
      loadFallbackConsent()
    }
    return false
  }, timeoutDuration)
}

function loadFallbackConsent() {
  console.log('loadFallbackConsent')
  const { gdpr } = APPConfig?.getAppConfig()
  const locale = getLocaleCodeFromUrl()
  let country = locale
  country = country?.split?.('_')?.[1]?.toLowerCase()

  const scriptUrl = gdpr?.scriptUrl
    ?.replace('{COUNTRY}', country)
    ?.replace('{LOCALE}', locale)

  const script = document.createElement('script')
  script.src = scriptUrl
  document.body.appendChild(script)
}

async function showConfigsAndVersion() {
  const isToShowConfig = convertToBoolean(
    APPConfig?.getAppConfig()?.showConfigs || false
  )

  if (IS_BROWSER && typeof process != undefined && process?.env?.SF_VERSION) {
    window.SF_VERSION = process.env.SF_VERSION
  }

  if (isToShowConfig) {
    const { showConfigInConsole } = await import('src/App/appConfigPrinting')
    showConfigInConsole()
  }
}

/**
 * Hides the Maze contextual widget by adding a style tag to the document head.
 * Implementation is part of ticket MDIGI-3262
 */

function hideChatbot() {
  const isToHideChatBotSurvey =
    convertToBoolean(getCookie('nofeedback')) || false

  if (isToHideChatBotSurvey) {
    const styleElement = document.createElement('style')
    styleElement.textContent = `
    #maze-contextual-widget-host
     {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
  `
    document.head.appendChild(styleElement)
  }
}

function setRedirectedAppsCookie() {
  try {
    const { appRedirects = '' } = APPConfig?.getAppConfig() || {}
    const country =
      getLocaleCodeFromUrl()?.split('_')?.[1]?.toUpperCase() || 'US'
    const appsRedirectMarket = getCookie('appRedirectsMarket') || ''

    if (appsRedirectMarket && appsRedirectMarket !== country) {
      deleteCookie('appRedirects')
      deleteCookie('appRedirectsMarket')
    }

    if (appRedirects?.static && Object.keys(appRedirects?.static)?.length > 0) {
      let appRedirectsCookie = getCookie('appRedirects') || ''
      appRedirectsCookie =
        appRedirectsCookie &&
        JSON.parse(decodeURIComponent(appRedirectsCookie || ''))

      if (!appRedirectsCookie) {
        setCookie({
          cookieName: 'appRedirects',
          cookieValue: encodeURIComponent(JSON.stringify(appRedirects)),
        })
        setCookie({
          cookieName: 'appRedirectsMarket',
          cookieValue: country,
        })
      }
    }
  } catch (e) {
    console.warn('Error setting appRedirects cookie:', e)
  }
}

export { i18n }
